import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/lib/api';
import { cn } from '@/lib/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

interface SubscriptionButtonProps {
  className?: string;
  isMobile?: boolean;
}

function formatExpiry(expiresAtIso: string): string | null {
  const expiresAt = new Date(expiresAtIso);
  const now = new Date();
  const ms = expiresAt.getTime() - now.getTime();
  const daysFloat = ms / (1000 * 60 * 60 * 24);
  const days = Math.floor(daysFloat);

  // Handle expired subscriptions
  if (daysFloat < 0) {
    const daysAgo = Math.abs(days);
    if (daysAgo === 0) return 'today';
    if (daysAgo === 1) return '1 day ago';
    return `${daysAgo} days ago`;
  }

  // Handle future expirations
  if (daysFloat <= 0) return 'today';
  if (days <= 0) return 'today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

export const SubscriptionButton: React.FC<SubscriptionButtonProps> = ({ className, isMobile }) => {
  const { user } = useAuth();
  const { api } = useApiClient();
  const [recentlyExpiredDate, setRecentlyExpiredDate] = useState<string | null>(null);

  // Check for recently expired subscription when user is in free mode
  useEffect(() => {
    const checkRecentlyExpired = async () => {
      if (user?.subscription_mode === 'free' && !user?.subscription_expires_at) {
        try {
          const res = await api.getMySubscription();
          if (res.subscription?.current_period_end) {
            const expiredDate = new Date(res.subscription.current_period_end);
            const now = new Date();
            const daysSinceExpiry = (now.getTime() - expiredDate.getTime()) / (1000 * 60 * 60 * 24);
            
            // Only track if expired within last 2 weeks (14 days)
            if (daysSinceExpiry >= 0 && daysSinceExpiry <= 14) {
              setRecentlyExpiredDate(res.subscription.current_period_end);
            } else {
              setRecentlyExpiredDate(null);
            }
          } else {
            setRecentlyExpiredDate(null);
          }
        } catch (error) {
          // Silently fail - don't show error for subscription check
          setRecentlyExpiredDate(null);
        }
      } else {
        setRecentlyExpiredDate(null);
      }
    };

    checkRecentlyExpired();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.subscription_mode, user?.subscription_expires_at]);

  const { showButton, label, variant } = useMemo(() => {
    // Default: show subscribe button
    let show = true;
    let text = 'Subscription';
    let style: 'primary' | 'warning' | 'danger' = 'primary';

    const mode = user?.subscription_mode ?? 'free';
    const expiresAt = user?.subscription_expires_at ?? null;

    if (mode === 'premium') {
      // Hide unless expiring within 7 days (inclusive)
      if (expiresAt) {
        const expiresDate = new Date(expiresAt);
        const now = new Date();
        const daysLeft = (expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
        
        // Check if already expired first
        if (daysLeft < 0) {
          // Subscription is expired but mode is still premium (edge case)
          // Show as expired with warning
          show = true;
          text = 'Subscription Expired';
          style = 'danger';
        } else if (daysLeft <= 7) {
          show = true;
          const expiresText = formatExpiry(expiresAt);
          text = expiresText === 'today' ? 'Subscription expires today' : `Subscription expires in ${expiresText}`;
          // 3 days or under -> danger (red). Otherwise warning (amber)
          style = daysLeft <= 3 ? 'danger' : 'warning';
        } else {
          show = false;
        }
      } else {
        // Premium without expiry info: hide by default
        show = false;
      }
    } else {
      // Free mode: check if recently expired
      if (recentlyExpiredDate) {
        // recentlyExpiredDate is only set if expired within last 2 weeks
        const expiredDate = new Date(recentlyExpiredDate);
        const now = new Date();
        const daysSinceExpiry = (now.getTime() - expiredDate.getTime()) / (1000 * 60 * 60 * 24);
        
        // Show renew button for recently expired subscriptions (within 2 weeks)
        show = true;
        text = 'Renew Subscription';
        // More urgent if expired recently (within 3 days), otherwise warning
        style = daysSinceExpiry <= 3 ? 'danger' : 'warning';
      } else {
        // Never subscribed or expired more than 2 weeks ago: normal subscribe button
        show = true;
        text = 'Subscription';
        style = 'primary';
      }
    }

    return { showButton: show, label: text, variant: style };
  }, [user?.subscription_mode, user?.subscription_expires_at, recentlyExpiredDate]);

  if (!showButton) return null;

  const baseClasses = cn(
    'flex items-center font-medium rounded-xl shadow-sm border transition-all duration-200 hover:shadow-md',
    isMobile ? 'gap-1 px-3 py-2' : 'gap-3 px-4 py-2.5',
    variant === 'primary'
      ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:border-blue-700'
      : variant === 'danger'
        ? 'bg-red-600 text-white border-red-600 hover:bg-red-700 hover:border-red-700'
        : 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100 hover:border-amber-400'
  );

  return (
    <Link to="/subscription" className={className}>
      <Button className={baseClasses} style={isMobile ? {} : { minWidth: 140 }}>
        <span className={cn('font-bold', isMobile ? 'text-sm' : 'text-sm')}>{label}</span>
      </Button>
    </Link>
  );
};

export default SubscriptionButton;
